/**
 * Alert centre strings (`alerts.*`) and notifications bell strings (`notif.*`), merged through `extra.ts`.
 * English objects are typed against the Polish ones, so a missing key is a compile error.
 * Alert severity / type / status labels live in `c360.alerts.*` (shared with the customer card).
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const alertsPl = {
  navLabel: 'Alerty',
  subtitle: 'Silnik alertów: spadki sprzedaży, brak zamówień, marża i okazje sprzedażowe.',
  openCount: '{count} otwartych',
  filters: {
    status: 'Status',
    severity: 'Ważność',
    type: 'Typ',
    owner: 'Handlowiec',
    allStatuses: 'Wszystkie',
    allSeverities: 'Każda ważność',
    allTypes: 'Wszystkie typy',
    allOwners: 'Wszyscy handlowcy',
  },
  actions: {
    ack: 'Potwierdź',
    resolve: 'Rozwiąż',
    openCustomer: 'Otwórz klienta',
    recompute: 'Przelicz alerty',
    acked: 'Alert potwierdzony',
    resolved: 'Alert rozwiązany',
    recomputed: 'Przeliczono alerty: {created} nowych, {resolved} rozwiązanych ({ms} ms)',
  },
  facts: {
    salesDrop: '{pct}: {current} vs {previous}',
    noOrder: '{days} bez zamówienia (zwykle co {interval})',
    marginDrop: 'Marża {current} vs {previous}',
    productLost: '{name}: {days} bez zakupu (zwykle co {interval})',
    crossSell: '{group}: kupuje {bought} z {total} produktów',
    visitDue: 'Kontakt zaległy o {days}',
  },
  detected: 'Wykryto {when}',
  empty: 'Brak alertów',
  emptyBody: 'Żaden alert nie spełnia wybranych kryteriów.',
  loadError: 'Nie udało się wczytać alertów.',
};

export type AlertsMessages = Shape<typeof alertsPl>;

export const alertsEn: AlertsMessages = {
  navLabel: 'Alerts',
  subtitle: 'Alert engine: sales drops, missing orders, margin and sales opportunities.',
  openCount: '{count} open',
  filters: {
    status: 'Status',
    severity: 'Severity',
    type: 'Type',
    owner: 'Sales rep',
    allStatuses: 'All',
    allSeverities: 'Any severity',
    allTypes: 'All types',
    allOwners: 'All reps',
  },
  actions: {
    ack: 'Acknowledge',
    resolve: 'Resolve',
    openCustomer: 'Open customer',
    recompute: 'Recompute alerts',
    acked: 'Alert acknowledged',
    resolved: 'Alert resolved',
    recomputed: 'Alerts recomputed: {created} new, {resolved} resolved ({ms} ms)',
  },
  facts: {
    salesDrop: '{pct}: {current} vs {previous}',
    noOrder: '{days} without an order (usually every {interval})',
    marginDrop: 'Margin {current} vs {previous}',
    productLost: '{name}: {days} without purchase (usually every {interval})',
    crossSell: '{group}: buys {bought} of {total} products',
    visitDue: 'Contact overdue by {days}',
  },
  detected: 'Detected {when}',
  empty: 'No alerts',
  emptyBody: 'No alert matches the selected criteria.',
  loadError: 'Could not load alerts.',
};

export const notifPl = {
  title: 'Powiadomienia',
  unread: '{count} nieprzeczytanych',
  markAll: 'Oznacz jako przeczytane',
  empty: 'Brak powiadomień',
  emptyBody: 'Tutaj pojawią się alerty, zadania i akceptacje.',
  loadError: 'Nie udało się wczytać powiadomień.',
};

export type NotifMessages = Shape<typeof notifPl>;

export const notifEn: NotifMessages = {
  title: 'Notifications',
  unread: '{count} unread',
  markAll: 'Mark all as read',
  empty: 'No notifications',
  emptyBody: 'Alerts, tasks and approvals will show up here.',
  loadError: 'Could not load notifications.',
};
